/**
 * Feb-2026 (User-Wunsch 27.05.2026) — Best-Practice-Tips-Card.
 *
 * Lädt 3-8 datengetriebene Handlungsempfehlungen vom Backend
 * (``GET /api/users/me/dealer-stats/tips``) und rendert sie als gelb-grun-rot
 * farbkodierte Tip-Tiles im Dealer-Dashboard. Jeder Tipp hat eine kurze
 * Beschreibung mit konkreten Zahlen und einen Deep-Link-Button.
 *
 * Severity-Farben:
 *   * ``critical`` — rot (z.B. Reservierung läuft in 24h ab, fehlende Belege)
 *   * ``warning``  — orange/gelb (z.B. offene Preisvorschläge, Inserate ohne Foto)
 *   * ``info``     — blau (z.B. Conversion-Tipps, Bewertungs-Empfehlungen)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb, AlertTriangle, AlertOctagon, Info, ArrowRight, Loader2, ChevronDown, ChevronUp, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const SEVERITY_STYLES = {
    critical: {
        bg: "bg-red-50 border-red-300",
        iconBg: "bg-red-100 text-red-700",
        Icon: AlertOctagon,
        badgeBg: "bg-red-600 text-white",
        badgeLabel: "Wichtig",
    },
    warning: {
        bg: "bg-amber-50 border-amber-300",
        iconBg: "bg-amber-100 text-amber-700",
        Icon: AlertTriangle,
        badgeBg: "bg-amber-500 text-white",
        badgeLabel: "Hinweis",
    },
    info: {
        bg: "bg-blue-50 border-blue-300",
        iconBg: "bg-blue-100 text-blue-700",
        Icon: Info,
        badgeBg: "bg-blue-600 text-white",
        badgeLabel: "Tipp",
    },
};

const STORAGE_KEY_DISMISSED = "twowheels.tips.dismissed_ids";

function readDismissed() {
    try {
        return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY_DISMISSED) || "[]"));
    } catch {
        return new Set();
    }
}

function writeDismissed(set) {
    try {
        localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(Array.from(set)));
    } catch { /* ignore */ }
}

export default function DealerTipsCard() {
    const [tips, setTips] = useState(null);
    const [error, setError] = useState(null);
    const [dismissed, setDismissed] = useState(readDismissed);
    const [expanded, setExpanded] = useState(true);
    // Feb-2026 (User-Wunsch 27.05.2026) — Wöchentlicher E-Mail-Digest opt-in.
    // Wert aus dem Dealer-Profil; toggelt sich über PATCH /users/me/dealer-profile.
    const [weeklyDigest, setWeeklyDigest] = useState(false);
    const [savingDigest, setSavingDigest] = useState(false);

    useEffect(() => {
        api.get("/users/me/dealer-stats/tips")
            .then((r) => setTips(r.data?.tips || []))
            .catch((e) => setError(e?.response?.data?.detail || "Tips konnten nicht geladen werden."));
        // Profil-Flag lazy laden (still kein Crash bei Fehler)
        api.get("/users/me/dealer-profile")
            .then((r) => setWeeklyDigest(!!r.data?.tips_email_weekly))
            .catch(() => { /* ignore */ });
    }, []);

    const toggleWeeklyDigest = async () => {
        const next = !weeklyDigest;
        setSavingDigest(true);
        try {
            await api.patch("/users/me/dealer-profile", { tips_email_weekly: next });
            setWeeklyDigest(next);
            toast.success(next
                ? "Wöchentlicher Tipps-Digest aktiviert — Sie erhalten ihn jeden Montag."
                : "Wöchentlicher Tipps-Digest deaktiviert.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Konnte Einstellung nicht speichern.");
        } finally {
            setSavingDigest(false);
        }
    };

    const dismissTip = (idx) => {
        // Tips haben keine stable ID — wir nutzen kpi+title als Pseudo-ID
        const tip = tips[idx];
        const id = `${tip.kpi}::${tip.title}`;
        const next = new Set(dismissed);
        next.add(id);
        setDismissed(next);
        writeDismissed(next);
    };

    if (error) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-4 text-sm text-gray-500" data-testid="tips-error">
                {error}
            </div>
        );
    }
    if (tips === null) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-4 text-sm text-gray-500 flex items-center gap-2" data-testid="tips-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Lade Empfehlungen …
            </div>
        );
    }

    const visibleTips = tips.filter((t) => !dismissed.has(`${t.kpi}::${t.title}`));
    if (visibleTips.length === 0) {
        return (
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 flex items-center gap-3" data-testid="tips-empty">
                <div className="w-8 h-8 rounded-sm bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <Lightbulb className="w-4 h-4" />
                </div>
                <div className="text-sm">
                    <div className="font-bold text-emerald-900">Alles im grünen Bereich!</div>
                    <div className="text-emerald-700 text-xs">Aktuell keine Best-Practice-Empfehlungen — Sie machen einen ausgezeichneten Job.</div>
                </div>
            </div>
        );
    }

    // Sortierung: critical > warning > info
    const sortKey = { critical: 0, warning: 1, info: 2 };
    const ordered = [...visibleTips].sort((a, b) => (sortKey[a.severity] ?? 9) - (sortKey[b.severity] ?? 9));

    return (
        <div className="bg-white border border-[#FFCC00]/40 rounded-sm overflow-hidden" data-testid="dealer-tips-card">
            <div className="w-full px-5 py-3 border-b border-gray-100 bg-[#FFCC00]/10 flex items-center justify-between gap-2">
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="flex items-center gap-2 flex-1 text-left hover:opacity-70 transition-opacity"
                    data-testid="tips-expand-toggle"
                    aria-expanded={expanded}
                >
                    <Lightbulb className="w-4 h-4 text-[#FFCC00]" />
                    <h3 className="font-display font-bold text-base">Best-Practice-Empfehlungen</h3>
                    <span className="text-[10px] uppercase tracking-wider text-gray-600 font-bold ml-2 px-1.5 py-0.5 bg-white border border-gray-200 rounded-sm">{ordered.length} aktiv</span>
                </button>
                {/* Feb-2026 (User-Wunsch 27.05.2026) — wöchentlicher Digest-Toggle */}
                <button
                    type="button"
                    onClick={toggleWeeklyDigest}
                    disabled={savingDigest}
                    className={`shrink-0 inline-flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider px-2 py-1.5 rounded-sm border-2 transition-colors disabled:opacity-60 ${
                        weeklyDigest
                            ? "bg-[#111827] text-[#FFCC00] border-[#111827]"
                            : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"
                    }`}
                    title={weeklyDigest
                        ? "Wöchentlicher E-Mail-Digest aktiv — jeden Montag erhalten Sie die Top-3-Tipps. Klicken zum Deaktivieren."
                        : "Aktivieren Sie den wöchentlichen E-Mail-Digest, um jeden Montag die Top-3-Tipps in Ihrem Postfach zu erhalten."}
                    data-testid="tips-weekly-digest-toggle"
                    aria-pressed={weeklyDigest}
                >
                    <Mail className="w-3 h-3" />
                    {weeklyDigest ? "Digest: AN" : "Digest: AUS"}
                </button>
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 uppercase tracking-wider"
                    data-testid="tips-expand-icon"
                    aria-label={expanded ? "Einklappen" : "Ausklappen"}
                >
                    {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
            </div>
            {expanded && (
                <ul className="divide-y divide-gray-100" data-testid="tips-list">
                    {ordered.map((tip, idx) => {
                        const style = SEVERITY_STYLES[tip.severity] || SEVERITY_STYLES.info;
                        const Icon = style.Icon;
                        return (
                            <li key={`${tip.kpi}-${idx}`} className={`p-4 ${style.bg} border-l-4`} data-testid={`tip-${tip.kpi}-${idx}`}>
                                <div className="flex items-start gap-3">
                                    <div className={`shrink-0 w-9 h-9 rounded-sm flex items-center justify-center ${style.iconBg}`}>
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <h4 className="font-bold text-sm text-[#111827]">{tip.title}</h4>
                                            <span className={`text-[9px] uppercase font-black tracking-wider px-1.5 py-0.5 rounded-sm ${style.badgeBg}`}>{style.badgeLabel}</span>
                                            <span className="text-[9px] uppercase font-bold tracking-wider text-gray-500">· KPI: {tip.kpi}</span>
                                        </div>
                                        <p className="text-sm text-gray-700 mb-2">{tip.body}</p>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            {tip.cta_url && tip.cta_label && (
                                                <Link
                                                    to={tip.cta_url}
                                                    className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#111827] bg-white border border-[#111827] px-3 py-1.5 rounded-sm hover:bg-[#111827] hover:text-[#FFCC00] transition-colors"
                                                    data-testid={`tip-cta-${tip.kpi}-${idx}`}
                                                >
                                                    {tip.cta_label} <ArrowRight className="w-3 h-3" />
                                                </Link>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => dismissTip(idx)}
                                                className="text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-[#111827]"
                                                data-testid={`tip-dismiss-${tip.kpi}-${idx}`}
                                            >
                                                Erledigt · Ausblenden
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
